/**
 * TRUST-PROVENANCE-001 (2026-05-28) — Deterministic tooltip composer.
 *
 * Pure function. No I/O. No state. No new data dependencies.
 *
 * Builds an owner-readable multi-line tooltip string from the existing
 * canonical accreditation projection (already shipped by the search +
 * profile endpoints). Used on:
 *
 *   - search compact chip       (TrustBadgeCompact-v1)
 *   - expanded panel row        (TrustCardFull-v1)
 *   - public profile chip       (trust-accreditation-{i})
 *
 * The output is rendered via the native `title=` attribute (no custom
 * tooltip component, no Radix popover, no portal) — keeps blast radius
 * at zero. Click-through to the canonical AccreditationEvidenceModal
 * (TRUST-001) remains the authoritative drill-down path; this tooltip
 * is a fast-glance provenance hint, not a replacement.
 *
 * Determinism: same accreditation object → same tooltip string.
 * Public-safety: only reads fields already in the public-safe payload
 * allow-list. Never reads doc_id, storage_path, or any forbidden key.
 */

function _formatExpiry(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) { return null; }
}

function _expiryStatus(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const ms = d.getTime() - Date.now();
    if (ms < 0) return 'expired';
    if (ms < 1000 * 60 * 60 * 24 * 30) return 'expiring_soon';
    return 'valid';
  } catch (_) { return null; }
}

/**
 * Compose the provenance tooltip for an accreditation.
 *
 * @param {object} acc – the patient-safe accreditation projection.
 *   Expected keys (any may be missing): slug, short_label, long_label,
 *   is_verified_new, is_self_attested, evidence {issuing_authority,
 *   expiry_date, verification_note, failure_reasons[]}.
 *
 * @returns {string} multi-line string ('\n'-joined) safe to drop into
 *   the native `title=` attribute. Returns the slug as a last resort
 *   so the tooltip is never empty.
 */
export function formatProvenanceTitle(acc) {
  if (!acc || typeof acc !== 'object') return '';
  const lines = [];
  const headName = acc.long_label || acc.short_label || acc.slug || 'Accreditation';
  lines.push(headName);

  const ev = (acc.evidence && typeof acc.evidence === 'object') ? acc.evidence : {};

  // Verification provenance — the headline trust signal.
  if (acc.is_verified_new === true && acc.is_self_attested !== true) {
    lines.push('✓ Verified by TreatX compliance review');
  } else if (acc.is_self_attested === true) {
    lines.push('• Self-reported by the clinic (not yet verified by TreatX)');
  } else {
    lines.push('• Verification pending');
  }

  // Issuing authority — public-safe field already in the allow-list.
  const authority = (typeof ev.issuing_authority === 'string' && ev.issuing_authority.trim())
    ? ev.issuing_authority.trim() : null;
  if (authority) {
    lines.push(`Issued by ${authority}`);
  }

  // Expiry — surface validity status without leaking raw doc internals.
  const expiry = _formatExpiry(ev.expiry_date);
  if (expiry) {
    const status = _expiryStatus(ev.expiry_date);
    if (status === 'expired') {
      lines.push(`⚠ Document expired on ${expiry}`);
    } else if (status === 'expiring_soon') {
      lines.push(`⚠ Expires soon — ${expiry}`);
    } else {
      lines.push(`Valid until ${expiry}`);
    }
  }

  // Self-attested failure reason (publicly safe — already projected as
  // category labels, not raw moderation internals).
  if (acc.is_self_attested === true && Array.isArray(ev.failure_reasons) && ev.failure_reasons.length > 0) {
    const fr = ev.failure_reasons;
    let reason = null;
    if (fr.includes('missing_source_document_id') || fr.includes('source_document_not_found')) {
      reason = 'Missing supporting document';
    } else if (fr.includes('document_expired')) {
      reason = 'Supporting document expired';
    } else if (fr.includes('document_superseded')) {
      reason = 'Supporting document superseded';
    } else if (fr.includes('document_not_approved')) {
      reason = 'Supporting document not yet approved';
    }
    if (reason) {
      lines.push(`Reason: ${reason}`);
    }
  }

  // Action hint.
  lines.push('Click for evidence details');

  return lines.join('\n');
}

/**
 * Short single-line provenance label for inline subtitles in the
 * expanded panel. Falls back to existing wording when fields missing.
 *
 * @param {object} acc - patient-safe accreditation projection.
 * @returns {string}
 */
export function formatProvenanceSubtitle(acc) {
  if (!acc || typeof acc !== 'object') return '';
  const ev = (acc.evidence && typeof acc.evidence === 'object') ? acc.evidence : {};
  const parts = [];
  if (acc.is_verified_new === true && acc.is_self_attested !== true) {
    parts.push('Verified by TreatX');
  } else if (acc.is_self_attested === true) {
    parts.push('Self-reported');
  }
  const authority = (typeof ev.issuing_authority === 'string' && ev.issuing_authority.trim())
    ? ev.issuing_authority.trim() : null;
  if (authority) parts.push(authority);
  const expiry = _formatExpiry(ev.expiry_date);
  if (expiry) {
    const status = _expiryStatus(ev.expiry_date);
    if (status === 'expired') parts.push(`expired ${expiry}`);
    else if (status === 'expiring_soon') parts.push(`expires soon (${expiry})`);
    else parts.push(`valid until ${expiry}`);
  }
  return parts.join(' · ');
}
